import {
  IonButton, IonButtons, IonCard, IonCardContent, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem,
  IonLabel, IonList, IonLoading, IonModal, IonNote, IonSpinner, IonText, IonTitle,
  IonToast, IonToggle, IonToolbar
} from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  apiMarketingBuscarConfiguracao, apiMarketingSalvarConfiguracao,
  apiMarketingTestarConexao, requestService
} from '../../../../../Utils/Services';

type Props = {
  isOpen: boolean;
  onDidDismiss: () => void;
  loja: string;
  onStatusChange?: (ativo: boolean) => void;
};

type FormState = {
  ativo: boolean;
  metaPixelId: string;
  metaCapiToken: string;
  rastrearWhatsapp: boolean;
  rastrearAbandono: boolean;
  rastrearProdutos: boolean;
  rastrearPedidos: boolean;
  exigirConsentimento: boolean;
  debug: boolean;
};

type ValidacaoStatus = {
  meta?: { validado: boolean; dataUltimaValidacao: string };
  ga4?: { validado: boolean; dataUltimaValidacao: string };
};

type TesteResultado = {
  meta: { ok: boolean };
  ga4: { ok: boolean };
  dataValidacao: string;
} | null;

const DEFAULT_STATE: FormState = {
  ativo: false,
  metaPixelId: '',
  metaCapiToken: '',
  rastrearWhatsapp: true,
  rastrearAbandono: true,
  rastrearProdutos: true,
  rastrearPedidos: true,
  exigirConsentimento: true,
  debug: false,
};

function getValidacaoKey(loja: string) {
  return `marketing_validacao_${loja}`;
}

function formatarData(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

type IntegracaoStatusType = 'nao-configurada' | 'configurada' | 'validada';

function getStatusMeta(pixelId: string, validacao: ValidacaoStatus): IntegracaoStatusType {
  if (!pixelId) return 'nao-configurada';
  if (validacao.meta?.validado) return 'validada';
  return 'configurada';
}

function StatusBadge({ status }: { status: IntegracaoStatusType }) {
  if (status === 'validada') return <span style={{ color: '#2dd36f', fontWeight: 600 }}>🟢 Validada</span>;
  if (status === 'configurada') return <span style={{ color: '#ffc409', fontWeight: 600 }}>🟡 Configurada</span>;
  return <span style={{ color: '#eb445a', fontWeight: 600 }}>🔴 Não configurada</span>;
}

const PIXEL_REGEX = /^\d{6,20}$/;

/**
 * Modal de configuração da integração Meta Ads (Facebook + Instagram).
 * Compartilha o mesmo endpoint backend (/empresas/{id}/configuracao/marketing)
 * com Google Analytics — cada modal envia apenas seus próprios campos.
 */
const IntegracaoMetaAdsModal: React.FC<Props> = ({ isOpen, onDidDismiss, loja, onStatusChange }) => {
  const history = useHistory();

  const [state, setState] = useState<FormState>(DEFAULT_STATE);
  const [originalCapiMasked, setOriginalCapiMasked] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [testando, setTestando] = useState(false);
  const [testeResultado, setTesteResultado] = useState<TesteResultado>(null);
  const [validacao, setValidacao] = useState<ValidacaoStatus>({});
  const [toastText, setToastText] = useState('');
  const [showToast, setShowToast] = useState(false);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setState(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!isOpen || !loja) return;
    const saved = localStorage.getItem(getValidacaoKey(loja));
    setValidacao(saved ? JSON.parse(saved) : {});
    setTesteResultado(null);
    setLoading(true);
    const url = apiMarketingBuscarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));
    requestService(
      url,
      { method: apiMarketingBuscarConfiguracao.method },
      history,
      (resp: any) => {
        setLoading(false);
        const item = (resp && resp.Item) || null;
        if (!item) {
          setState(DEFAULT_STATE);
          setOriginalCapiMasked('');
          return;
        }
        setOriginalCapiMasked(item.metaCapiToken || '');
        const ativo = item.metaAdsAtivo === true || (item.metaAdsAtivo === undefined && item.ativo === true);
        setState({
          ativo,
          metaPixelId: item.metaPixelId || '',
          metaCapiToken: item.metaCapiToken || '',
          rastrearWhatsapp: item.rastrearWhatsapp !== false,
          rastrearAbandono: item.rastrearAbandono !== false,
          rastrearProdutos: item.rastrearProdutos !== false,
          rastrearPedidos: item.rastrearPedidos !== false,
          exigirConsentimento: item.exigirConsentimento !== false,
          debug: !!item.debug,
        });
      },
      (err: any) => {
        setLoading(false);
        console.log('[MetaAds] erro buscar configuração', err);
      }
    );
  }, [isOpen, loja]);

  const validar = (): string | null => {
    if (state.metaPixelId && !PIXEL_REGEX.test(state.metaPixelId)) {
      return 'Pixel ID inválido (somente dígitos, 6 a 20 caracteres).';
    }
    if (state.ativo && !state.metaPixelId) {
      return 'Informe o Pixel ID para ativar a integração Meta Ads.';
    }
    return null;
  };

  const salvar = () => {
    const erro = validar();
    if (erro) {
      setToastText(erro);
      setShowToast(true);
      return;
    }
    setLoading(true);
    const url = apiMarketingSalvarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));

    const body: any = {
      metaAdsAtivo: state.ativo,
      metaPixelId: state.metaPixelId,
      rastrearWhatsapp: state.rastrearWhatsapp,
      rastrearAbandono: state.rastrearAbandono,
      rastrearProdutos: state.rastrearProdutos,
      rastrearPedidos: state.rastrearPedidos,
      exigirConsentimento: state.exigirConsentimento,
      debug: state.debug,
    };
    if (state.metaCapiToken !== originalCapiMasked) body.metaCapiToken = state.metaCapiToken;

    requestService(
      url,
      {
        method: apiMarketingSalvarConfiguracao.method,
        body: JSON.stringify(body),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      (_resp: any) => {
        setLoading(false);
        setToastText('Configurações Meta Ads salvas.');
        setShowToast(true);
        if (onStatusChange) onStatusChange(state.ativo);
        onDidDismiss();
      },
      (err: any) => {
        setLoading(false);
        setToastText((err && err.message) || 'Erro ao salvar configurações.');
        setShowToast(true);
      }
    );
  };

  const executarTeste = () => {
    if (testando) return;
    setTestando(true);
    setTesteResultado(null);
    requestService(
      apiMarketingTestarConexao.url.replace('{id}', loja.replaceAll('#', '%23')),
      {
        method: apiMarketingTestarConexao.method,
        body: JSON.stringify({ empresaId: loja }),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      (resp: any) => {
        setTestando(false);
        const dataValidacao = new Date().toISOString();
        const metaOk = resp.meta?.ok === true && (resp.meta?.response?.events_received ?? 0) > 0;
        let ga4Ok = false;

        if (resp.ga4?.ok === true) {
          const status = resp.ga4?.response?.status;

          if (status === 204) {
            ga4Ok = true;
          } else if (status === 200) {
            try {
              const body = JSON.parse(resp.ga4.response.body || '{}');

              ga4Ok = Array.isArray(body.validationMessages) &&
                body.validationMessages.length === 0;
            } catch {
              ga4Ok = false;
            }
          }
        }
        const resultado: TesteResultado = {
          meta: { ok: metaOk },
          ga4: { ok: ga4Ok },
          dataValidacao,
        };
        setTesteResultado(resultado);
        const key = getValidacaoKey(loja);
        const existing: ValidacaoStatus = JSON.parse(localStorage.getItem(key) || '{}');
        const updated: ValidacaoStatus = {
          meta: metaOk ? { validado: true, dataUltimaValidacao: dataValidacao } : existing.meta,
          ga4: ga4Ok ? { validado: true, dataUltimaValidacao: dataValidacao } : existing.ga4,
        };
        localStorage.setItem(key, JSON.stringify(updated));
        setValidacao(updated);
      },
      (err: any) => {
        setTestando(false);
        setTesteResultado({ meta: { ok: false }, ga4: { ok: false }, dataValidacao: new Date().toISOString() });
        setToastText((err && err.message) || 'Erro ao executar teste.');
        setShowToast(true);
      },
      false
    );
  };

  const statusMeta = getStatusMeta(state.metaPixelId, validacao);

  const desativar = () => {
    setLoading(true);
    const url = apiMarketingSalvarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));
    requestService(
      url,
      {
        method: apiMarketingSalvarConfiguracao.method,
        body: JSON.stringify({ metaAdsAtivo: false }),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      () => {
        setLoading(false);
        setToastText('Integração Meta Ads desativada.');
        setShowToast(true);
        if (onStatusChange) onStatusChange(false);
        onDidDismiss();
      },
      (err: any) => {
        setLoading(false);
        setToastText((err && err.message) || 'Erro ao desativar.');
        setShowToast(true);
      }
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start" onClick={onDidDismiss}>
            <IonIcon slot="icon-only" icon={arrowBack} />
          </IonButtons>
          <IonTitle>Meta Ads</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding integracoes-modal-content">
        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Meta Ads</h2>
              <IonNote>Integre Facebook e Instagram Ads para rastrear conversões e melhorar suas campanhas.</IonNote>
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Status</h2>
            </div>
            <IonList className="integracoes-list">
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Integração ativa</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.ativo}
                  onIonChange={(e) => set('ativo', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="none" className="integracoes-option">
                <IonLabel className="ion-text-wrap">
                  <IonNote>Status da integração</IonNote>
                  <div className="integracoes-status-badge"><StatusBadge status={statusMeta} /></div>
                  {validacao.meta?.dataUltimaValidacao && (
                    <p>Última validação: {formatarData(validacao.meta.dataUltimaValidacao)}</p>
                  )}
                </IonLabel>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Credenciais</h2>
            </div>
            <div className="integracoes-field">
              <div className="integracoes-field-label">Pixel ID</div>
              <IonInput
                placeholder="123456789012345"
                value={state.metaPixelId}
                onIonInput={(e: any) => set('metaPixelId', (e.target.value || '').trim())}
              />
              <div className="integracoes-field-note">
                Rastreie acessos e conversões do Facebook/Instagram Ads.
              </div>
            </div>
            <div className="integracoes-field">
              <div className="integracoes-field-label">Token da API de Conversões (opcional)</div>
              <IonInput
                type="password"
                placeholder="EAABxxxxxxxxxxxxx"
                value={state.metaCapiToken}
                onIonInput={(e: any) => set('metaCapiToken', e.target.value || '')}
              />
              <div className="integracoes-field-note">
                Envia eventos diretamente para a Meta com maior precisão (server-side).
              </div>
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Eventos rastreados</h2>
            </div>
            <IonList className="integracoes-list">
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Cliques no WhatsApp</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.rastrearWhatsapp}
                  onIonChange={(e) => set('rastrearWhatsapp', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Abandono de carrinho</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.rastrearAbandono}
                  onIonChange={(e) => set('rastrearAbandono', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Visualização de produtos</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.rastrearProdutos}
                  onIonChange={(e) => set('rastrearProdutos', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="none" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Finalização de pedidos</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.rastrearPedidos}
                  onIonChange={(e) => set('rastrearPedidos', e.detail.checked)}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Privacidade</h2>
            </div>
            <IonList className="integracoes-list">
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Exigir consentimento LGPD</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.exigirConsentimento}
                  onIonChange={(e) => set('exigirConsentimento', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="none" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Modo debug</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.debug}
                  onIonChange={(e) => set('debug', e.detail.checked)}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {testeResultado && (
          <div style={{ marginTop: 16 }}>
            <IonText><h4 style={{ marginBottom: 8 }}>Resultado do teste</h4></IonText>
            <div style={{ background: 'var(--ion-color-light)', borderRadius: 8, padding: '12px 16px', marginBottom: 8 }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Meta Ads</p>
              {testeResultado.meta.ok
                ? <p style={{ margin: 0, color: '#2dd36f' }}>✅ Integração validada — evento recebido pela Meta com sucesso.</p>
                : <p style={{ margin: 0, color: '#eb445a' }}>❌ Falha na integração — verifique Pixel ID e Token da API de Conversões.</p>
              }
            </div>
            {/* <div style={{ background: 'var(--ion-color-light)', borderRadius: 8, padding: '12px 16px' }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Google Analytics</p>
              {testeResultado.ga4.ok
                ? <p style={{ margin: 0, color: '#2dd36f' }}>✅ Integração validada — evento recebido pelo Google Analytics com sucesso.</p>
                : <p style={{ margin: 0, color: '#eb445a' }}>❌ Falha na integração — verifique Measurement ID e API Secret.</p>
              }
            </div> */}
          </div>
        )}

        <IonLoading isOpen={loading} message="Aguarde..." />
        <IonToast
          isOpen={showToast}
          message={toastText}
          duration={3000}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8 }}>
            <IonButton
              expand="block"
              color="medium"
              fill="outline"
              onClick={executarTeste}
              disabled={testando || !state.metaPixelId}
            >
              {testando && <IonSpinner name="crescent" style={{ marginRight: 8 }} />}
              {testando ? 'Testando...' : 'Executar teste de integração'}
            </IonButton>
            <IonButton expand="block" color="primary" onClick={salvar}>
              Salvar
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default IntegracaoMetaAdsModal;
